/**
 * In-memory notifications client for Vitest / demo mode. Records the last
 * subscription so tests can assert the registration payload.
 */

import type { ApiEnvelope } from '../../domain/types';
import type { AppNotification } from '@zunelo/shared';
import type { ClientConfig, INotificationsClient } from './types';
import { DEFAULT_CONFIG, simulateNetwork } from './mockClientUtils';

/** Valid base64url VAPID-shaped key (65 bytes when decoded) for the mock path. */
export const MOCK_VAPID_PUBLIC_KEY =
  'BBAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA';

export class MockNotificationsClient implements INotificationsClient {
  lastSubscription: PushSubscriptionJSON | null = null;
  testNotificationsSent = 0;

  constructor(
    private config: ClientConfig = DEFAULT_CONFIG,
    private readonly demoNow = new Date(),
  ) {}

  async listNotifications(): Promise<ApiEnvelope<AppNotification[]>> {
    return simulateNetwork(
      () => [
        {
          id: 'demo-github-review',
          source: 'github',
          title: 'Te pidieron revisar zunelo #336',
          body: 'Centro de notificaciones y actividad reciente.',
          itemId: 'judasane~zunelo~336',
          appPath: '/?view=code&item=judasane%7Ezunelo%7E336',
          externalUrl: 'https://github.com/judasane/zunelo/pull/336',
          createdAt: new Date(this.demoNow.getTime() - 12 * 60 * 1000).toISOString(),
          updatedAt: new Date(this.demoNow.getTime() - 12 * 60 * 1000).toISOString(),
          occurrenceCount: 1,
        },
        {
          id: 'demo-slack-thread',
          source: 'slack',
          title: 'Te nombraron en Slack',
          body: '¿Puedes confirmar el estado de la sincronización?',
          itemId: 'sl-101',
          appPath: '/?view=messages&item=sl-101',
          externalUrl: 'https://slack.com/',
          createdAt: new Date(this.demoNow.getTime() - 38 * 60 * 1000).toISOString(),
          updatedAt: new Date(this.demoNow.getTime() - 38 * 60 * 1000).toISOString(),
          occurrenceCount: 1,
        },
      ],
      this.config,
    );
  }

  async getVapidPublicKey(): Promise<ApiEnvelope<{ key: string }>> {
    return simulateNetwork(() => ({ key: MOCK_VAPID_PUBLIC_KEY }), this.config);
  }

  async registerPushSubscription(
    subscription: PushSubscriptionJSON,
  ): Promise<ApiEnvelope<{ ok: boolean }>> {
    return simulateNetwork(() => {
      this.lastSubscription = subscription;
      return { ok: true };
    }, this.config);
  }

  async sendTestNotification(): Promise<ApiEnvelope<{ ok: boolean; sent?: number }>> {
    return simulateNetwork(() => {
      this.testNotificationsSent += 1;
      return { ok: true, sent: this.lastSubscription ? 1 : 0 };
    }, this.config);
  }
}
